import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Collapse from 'components/global/Collapse';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';

import { Breakpoints } from 'utils/const.styles';

import { faAngleUp } from '@fortawesome/pro-solid-svg-icons';

import * as S from './TotalPrice.styled';

const TotalPrice = ({ withMyRates }) => {
	const { watch } = useFormContext();
	const { t } = useTranslation();

	const { feePerOrder, remainingAmount: userCredit } = useSelector((state) => state.user);

	const [availableFreeShipments, setAvailableFreeShipments] = useState(null);

	useEffect(() => {
		const fetchData = async () => {
			const { availableFreeShipments } = await fetchRemainingAmount();
			setAvailableFreeShipments(availableFreeShipments);
		};
		fetchData();
	}, []);

	const totalPrice = withMyRates
		? watch('items')?.filter((item) => !item.shipmentID).length * feePerOrder
		: watch('items')?.reduce((acc, item) => acc + (item.shipmentID ? 0 : (item.price ?? 0)), 0);

	const totalAccordionRef = useRef(null);
	const availableFreeShipmetsAccordionRef = useRef(null);
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const numberOfValidFreeShipments = availableFreeShipments?.reduce((acc, shipment) => {
		if (shipment.remainingShipments) {
			return acc + shipment.remainingShipments;
		}
		return acc;
	}, 0);

	const numebofFreeShipmentsToBeUsed = Math.min(
		watch('items')?.filter((item) => !item.shipmentID).length,
		numberOfValidFreeShipments
	);

	const leftToPayShipmentsAmount =
		watch('items')?.filter((item) => !item.shipmentID).length - numberOfValidFreeShipments;
	const leftToPay = leftToPayShipmentsAmount * feePerOrder;

	return withMyRates && availableFreeShipments && numberOfValidFreeShipments ? (
		<S.TotalPriceContainer>
			{availableFreeShipments && (
				<>
					{isMobile ? (
						<Collapse buttonRef={availableFreeShipmetsAccordionRef} defaultVisible>
							<S.TotalPriceRow>
								<Typography className="mx-4">availableFreeShipments</Typography>
								<Typography withCurrency>{numberOfValidFreeShipments}</Typography>
							</S.TotalPriceRow>
						</Collapse>
					) : (
						<S.TotalPriceRow>
							<Typography fontWeight="500">availableFreeShipments</Typography>
							<Typography>
								{numberOfValidFreeShipments} {t('shipments')}
							</Typography>
						</S.TotalPriceRow>
					)}
					<S.TotalPriceHr className="mobile-hidden" />
					<S.TotalPriceRow ref={isMobile ? availableFreeShipmetsAccordionRef : undefined}>
						<div style={{ display: 'flex', alignItems: 'center' }}>
							{isMobile && <S.ExpandIcon className={'expandIcon'} icon={faAngleUp} size="2x" />}
							<Typography fontWeight="500">shipmentsToBeUsed</Typography>
						</div>
						<Typography>
							{numebofFreeShipmentsToBeUsed} {t('shipments')}
						</Typography>
					</S.TotalPriceRow>
				</>
			)}
			{leftToPayShipmentsAmount > 0 && (
				<>
					<S.TotalPriceHr className="mobile-hidden" />
					{isMobile ? (
						<Collapse buttonRef={totalAccordionRef} defaultVisible>
							<S.TotalPriceRow>
								<Typography className="mx-4">availableCredits</Typography>
								<Typography withCurrency>{userCredit}</Typography>
							</S.TotalPriceRow>
						</Collapse>
					) : (
						<S.TotalPriceRow>
							<Typography fontWeight="500">availableCredits</Typography>
							<Typography withCurrency>{userCredit}</Typography>
						</S.TotalPriceRow>
					)}
					<S.TotalPriceHr className="mobile-hidden" />
					<S.TotalPriceRow ref={isMobile ? totalAccordionRef : undefined}>
						<div style={{ display: 'flex', alignItems: 'center' }}>
							{isMobile && <S.ExpandIcon className={'expandIcon'} icon={faAngleUp} size="2x" />}
							<Typography fontWeight="500">totalCredit</Typography>
						</div>
						<Typography withCurrency>{leftToPay}</Typography>
					</S.TotalPriceRow>
				</>
			)}
		</S.TotalPriceContainer>
	) : (
		<S.TotalPriceContainer>
			{isMobile ? (
				<Collapse buttonRef={totalAccordionRef} defaultVisible>
					<S.TotalPriceRow>
						<Typography className="mx-4">availableCredits</Typography>
						<Typography withCurrency>{userCredit}</Typography>
					</S.TotalPriceRow>
				</Collapse>
			) : (
				<S.TotalPriceRow>
					<Typography fontWeight="500">availableCredits</Typography>
					<Typography withCurrency>{userCredit}</Typography>
				</S.TotalPriceRow>
			)}

			<S.TotalPriceHr className="mobile-hidden" />
			<S.TotalPriceRow ref={isMobile ? totalAccordionRef : undefined}>
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{isMobile && <S.ExpandIcon className={'expandIcon'} icon={faAngleUp} size="2x" />}
					<Typography fontWeight="500">totalCredit</Typography>
				</div>
				<Typography withCurrency>{totalPrice}</Typography>
			</S.TotalPriceRow>
		</S.TotalPriceContainer>
	);
};

export default TotalPrice;
